import React, { useContext, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CursorContext } from "../context/CursorContext";
import { ScrollLink } from "../components/ScrollLink";
import { transition1 } from "../transitions";
import { projects, projectCategories } from "../Data";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, FreeMode } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  BsArrowRight,
  BsGithub,
  BsBoxArrowUpRight,
  BsX,
  BsStarFill,
  BsChevronLeft,
  BsChevronRight,
} from "react-icons/bs";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

// Category label -> accent color, sourced from projectCategories (falls back to violet)
const getCategoryColor = (category) =>
  projectCategories.find((c) => c.label === category)?.color || "#8b5cf6";

const Portfolio = () => {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);
  const [selected, setSelected] = useState(null);
  const [activeCategory, setActiveCategory] = useState("Starred");

  // ── External nav-button refs (category tabs carousel) ──
  const [catPrevEl, setCatPrevEl] = useState(null);
  const [catNextEl, setCatNextEl] = useState(null);

  // ── External nav-button refs (project carousel) ──
  const [projPrevEl, setProjPrevEl] = useState(null);
  const [projNextEl, setProjNextEl] = useState(null);

  const filteredProjects = useMemo(() => {
    if (activeCategory === "Starred") return projects.filter((p) => p.featured);
    if (activeCategory === "All") return projects;
    return projects.filter((p) => p.category === activeCategory);
  }, [activeCategory]);

  return (
    <motion.section
      id="portfolio"
      className="section snap-start min-h-screen flex flex-col lg:pt-36 pt-24 pb-20 overflow-y-auto"
      initial={{ opacity: 0, y: "5%" }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount: 0.15 }}
      exit={{ opacity: 0, y: "5%" }}
      transition={transition1}
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      <div className="container mx-auto px-6 flex flex-col h-full max-w-6xl">
        {/* ── Header ── */}
        <motion.div
          className="mb-6 shrink-0"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          <motion.h1 className="h1 font-secondary" variants={fadeUp} custom={0}>
            My Work
          </motion.h1>
          <motion.p
            className="mt-2 text-sm leading-relaxed max-w-4xl"
            variants={fadeUp}
            custom={1}
          >
            {projects.length}+ projects spanning full-stack apps, AI agents,
            eCommerce platforms, and more — pick a category to explore.
          </motion.p>
        </motion.div>

        {/* ── Category filter carousel ── */}
        <motion.div
          className="shrink-0 mb-6 flex items-center gap-2"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <button
            ref={(node) => setCatPrevEl(node)}
            aria-label="Scroll categories left"
            onMouseEnter={mouseEnterHandler}
            onMouseLeave={mouseLeaveHandler}
            className="cstm-nav-btn"
          >
            <BsChevronLeft size={13} />
          </button>

          <div className="flex-1 min-w-0">
            <Swiper
              modules={[Navigation, FreeMode]}
              navigation={{ prevEl: catPrevEl, nextEl: catNextEl }}
              freeMode={{ enabled: true, momentum: true }}
              slidesPerView="auto"
              spaceBetween={8}
              className="category-swiper"
            >
              {projectCategories.map((cat) => {
                const isActive = activeCategory === cat.label;
                const dotColor = cat.color || "#8b5cf6";
                return (
                  <SwiperSlide key={cat.label} style={{ width: "auto" }}>
                    <button
                      onClick={() => setActiveCategory(cat.label)}
                      onMouseEnter={mouseEnterHandler}
                      onMouseLeave={mouseLeaveHandler}
                      className={`shrink-0 flex items-center gap-2 rounded-full px-4 py-2 text-xs sm:text-sm font-medium border transition-all duration-300 whitespace-nowrap ${
                        isActive
                          ? "bg-purple-500 border-purple-500 text-white shadow-[0_4px_14px_-4px_rgba(139,92,246,0.6)]"
                          : "bg-white/40 dark:bg-white/5 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-purple-400/60 hover:text-purple-500 dark:hover:text-purple-300"
                      }`}
                    >
                      {cat.label === "Starred" && (
                        <BsStarFill
                          size={11}
                          className={isActive ? "text-white" : "text-amber-400"}
                        />
                      )}
                      {cat.label !== "All" && cat.label !== "Starred" && (
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{
                            backgroundColor: isActive ? "#fff" : dotColor,
                          }}
                        />
                      )}
                      {cat.label}
                      {cat.label === "Starred" && (
                        <span
                          className={`text-[10px] ${
                            isActive
                              ? "text-white/80"
                              : "text-slate-400 dark:text-slate-500"
                          }`}
                        >
                          {projects.filter((p) => p.featured).length}
                        </span>
                      )}
                      {cat.label !== "All" && cat.label !== "Starred" && (
                        <span
                          className={`text-[10px] ${
                            isActive
                              ? "text-white/80"
                              : "text-slate-400 dark:text-slate-500"
                          }`}
                        >
                          {
                            projects.filter((p) => p.category === cat.label)
                              .length
                          }
                        </span>
                      )}
                    </button>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>

          <button
            ref={(node) => setCatNextEl(node)}
            aria-label="Scroll categories right"
            onMouseEnter={mouseEnterHandler}
            onMouseLeave={mouseLeaveHandler}
            className="cstm-nav-btn"
          >
            <BsChevronRight size={13} />
          </button>
        </motion.div>

        {/* ── Project carousel toolbar (count + arrows) ── */}
        <motion.div
          className="shrink-0 flex items-center justify-between mb-3"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {filteredProjects.length}{" "}
            {filteredProjects.length === 1 ? "project" : "projects"}
          </span>
          <div className="flex items-center gap-2">
            <button
              ref={(node) => setProjPrevEl(node)}
              aria-label="Previous projects"
              onMouseEnter={mouseEnterHandler}
              onMouseLeave={mouseLeaveHandler}
              className="cstm-nav-btn"
            >
              <BsChevronLeft size={13} />
            </button>
            <button
              ref={(node) => setProjNextEl(node)}
              aria-label="Next projects"
              onMouseEnter={mouseEnterHandler}
              onMouseLeave={mouseLeaveHandler}
              className="cstm-nav-btn"
            >
              <BsChevronRight size={13} />
            </button>
          </div>
        </motion.div>

        {/* ── Project carousel ── */}
        <motion.div
          className="flex-1 min-h-0 flex flex-col"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.15 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div className="flex-1 min-h-0">
            <Swiper
              key={activeCategory}
              modules={[Navigation, Pagination]}
              navigation={{ prevEl: projPrevEl, nextEl: projNextEl }}
              pagination={{ clickable: true, el: ".portfolio-pagination" }}
              spaceBetween={24}
              slidesPerView={1}
              breakpoints={{
                640: { slidesPerView: 1.4 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 2.5 },
              }}
              className="w-full h-full portfolio-swiper"
            >
              {filteredProjects.map((project) => {
                const accent = getCategoryColor(project.category);
                return (
                  <SwiperSlide key={project.title} className="h-auto">
                    <motion.div
                      className="relative rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/40 dark:bg-white/5 backdrop-blur-sm overflow-hidden flex flex-col group cursor-pointer h-full"
                      whileHover={{ y: -5, transition: { duration: 0.22 } }}
                      onClick={() => setSelected(project)}
                    >
                      {/* thumbnail */}
                      <div className="relative overflow-hidden h-44 bg-slate-100 dark:bg-slate-800 flex-shrink-0">
                        <motion.img
                          src={project.img}
                          alt={project.title}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.06 }}
                          transition={{ duration: 0.4 }}
                        />

                        {/* category badge */}
                        <span
                          className="absolute top-3 left-3 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide backdrop-blur-sm"
                          style={{
                            backgroundColor: accent + "cc",
                            color: "#0F0A1E",
                          }}
                        >
                          {project.category}
                        </span>

                        {/* starred badge */}
                        {project.featured && (
                          <span
                            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-amber-400 flex items-center justify-center shadow-md"
                            title="Featured project"
                          >
                            <BsStarFill size={11} className="text-[#0F0A1E]" />
                          </span>
                        )}

                        {/* hover icon overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                          {project.liveLink !== "#" && (
                            <a
                              href={project.liveLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition"
                            >
                              <BsBoxArrowUpRight size={15} />
                            </a>
                          )}
                          {project.githubLink !== "#" && (
                            <a
                              href={project.githubLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition"
                            >
                              <BsGithub size={15} />
                            </a>
                          )}
                        </div>
                      </div>

                      {/* card body */}
                      <div className="p-5 flex flex-col gap-3 flex-1">
                        <h3 className="text-base font-semibold font-primary">
                          {project.title}
                        </h3>
                        <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400 flex-1 line-clamp-3">
                          {project.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {project.tech.slice(0, 3).map((tag, j) => (
                            <span
                              key={j}
                              className="rounded-full bg-purple-400/10 px-3 py-1 text-xs font-medium text-purple-600 dark:text-purple-300 border border-purple-400/20"
                            >
                              {tag}
                            </span>
                          ))}
                          {project.tech.length > 3 && (
                            <span className="rounded-full px-3 py-1 text-xs font-medium text-slate-400 dark:text-slate-500">
                              +{project.tech.length - 3}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* bottom accent */}
                      <div
                        className="absolute inset-x-0 bottom-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ backgroundColor: accent }}
                      />
                    </motion.div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>

          {/* ── Pagination dots — own row, so it never overlaps the cards ── */}
          <div className="portfolio-pagination flex items-center justify-center gap-2 mt-4 sm:mt-5 shrink-0" />
        </motion.div>

        {/* ── CTA ── */}
        <motion.div
          className="shrink-0 mt-6 mb-1"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <ScrollLink
            section="contact"
            className="btn max-w-fit hover:scale-105 transition-transform duration-300"
          >
            Let's Work Together
            <span className="ml-1">
              <BsArrowRight />
            </span>
          </ScrollLink>
        </motion.div>
      </div>

      {/* ── Project Detail Modal ── */}
      <AnimatePresence>
        {selected && (
          <>
            {/* backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
            />

            {/* panel */}
            <motion.div
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 60 }}
              transition={{ duration: 0.32, ease: "easeOut" }}
            >
              <div
                className="bg-beige dark:bg-mate w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden relative max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* close btn */}
                <button
                  className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-600 transition"
                  onClick={() => setSelected(null)}
                >
                  <BsX size={18} />
                </button>

                {/* image */}
                <div className="h-52 bg-slate-100 dark:bg-slate-800 overflow-hidden relative">
                  <img
                    src={selected.img}
                    alt={selected.title}
                    className="w-full h-full object-cover"
                  />
                  <span
                    className="absolute top-4 left-4 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide backdrop-blur-sm"
                    style={{
                      backgroundColor:
                        getCategoryColor(selected.category) + "cc",
                      color: "#0F0A1E",
                    }}
                  >
                    {selected.category}
                  </span>
                </div>

                {/* body */}
                <div className="p-6 flex flex-col gap-4">
                  <div>
                    <h2 className="text-xl font-bold font-primary mb-1">
                      {selected.title}
                    </h2>
                    <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                      {selected.description}
                    </p>
                  </div>

                  {/* tech */}
                  <div className="flex flex-wrap gap-2">
                    {selected.tech.map((tag, i) => (
                      <span
                        key={i}
                        className="rounded-full bg-purple-400/10 px-3 py-1 text-xs font-medium text-purple-600 dark:text-purple-300 border border-purple-400/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* links */}
                  <div className="flex gap-3 pt-1 flex-wrap">
                    {selected.liveLink !== "#" && (
                      <a
                        href={selected.liveLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn text-sm px-5 h-12"
                      >
                        Live Project
                        <span className="ml-2">
                          <BsBoxArrowUpRight />
                        </span>
                      </a>
                    )}
                    {selected.githubLink !== "#" && (
                      <a
                        href={selected.githubLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn text-sm px-5 h-12"
                      >
                        GitHub
                        <span className="ml-2">
                          <BsGithub />
                        </span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.section>
  );
};

export default Portfolio;
